import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import SearchBar from "../../../../../../../components/Filters/SearchBarWithFilters";
import DropdownSelect from "../../../../../../../components/Inputs/DropdownSelect";
import SubtleButton from "../../../../../../../components/Buttons/SubtleButton";
import { useDismissiblePopover } from "../../../../../../../components/Inputs/useDismissiblePopover";
import { resolveRouteColorPalette } from "../../../../../../../components/Maps/ActiveRoutes/routeColor";
import type {
  RecencyFilter,
  RouteBuilderProperty,
} from "../../../../../../../types/routeBuilder";
import { formatAddress } from "../../../../RoutePage/Routes/utils";
import { useRouteBuilder } from "../../../context/RouteBuilderContext";
import { useAddUnassignedCatalog } from "../../../hooks/useAddUnassignedCatalog";
import {
  defaultAddUnassignedFilters,
  selectableCatalogProperties,
  sortAddUnassignedCatalog,
  UNASSIGNED_SORT_OPTIONS,
} from "../../../lib/addUnassignedCatalog";
import {
  formatEstimatedServiceTimeFromSeconds,
  incorporatesTime,
  propertySearchRouteChips,
} from "../../../lib/helpers";
import { RECENCY_OPTIONS, serviceTypeLabel } from "../../../lib/serviceHelpers";
import type { UnassignedSort } from "../../../types/routeBuilderTypes";

export default function AddToUnassignedPopover({
  anchorRef,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}) {
  const { session, addPropertiesToUnassigned } = useRouteBuilder();
  const catalog = useAddUnassignedCatalog();
  const { filters, setFilters } = catalog;
  const rootRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });
  const [selected, setSelected] = useState<RouteBuilderProperty[]>([]);
  const close = useCallback(() => {
    onClose();
    anchorRef.current
      ?.querySelector<HTMLButtonElement>(
        "[data-testid='add-unassigned-properties']",
      )
      ?.focus();
  }, [onClose, anchorRef]);
  useDismissiblePopover(true, rootRef, close);

  useLayoutEffect(() => {
    const position = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(448, window.innerWidth - 24);
      const top = Math.max(12, Math.min(rect.top, window.innerHeight - 320));
      setStyle({
        position: "fixed",
        width,
        left: Math.max(12, rect.left - width - 12),
        top,
        maxHeight: window.innerHeight - top - 12,
      });
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [anchorRef]);

  const pooledIds = useMemo(
    () => new Set(session?.unroutedJobs.map((job) => job.jobId)),
    [session?.unroutedJobs],
  );
  const eligible = selectableCatalogProperties(
    catalog.properties,
    session?.unroutedJobs ?? [],
  );
  // Selection survives filter changes; Add still follows the current sort order.
  const selection = sortAddUnassignedCatalog(
    selected.filter((property) => !pooledIds.has(property.jobId)),
    filters.sort,
  );
  const selectedIds = new Set(selection.map((property) => property.jobId));
  if (!session) return null;

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-labelledby="add-unassigned-title"
      data-testid="add-unassigned-popover"
      style={style}
      className="z-[60] flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
    >
      <header className="shrink-0 border-b border-slate-200 p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3
              id="add-unassigned-title"
              className="text-sm font-semibold text-snow-primary"
            >
              Add properties to Unassigned
            </h3>
            <p className="text-xs text-slate-600">Tick as many as you like.</p>
          </div>
          <button
            type="button"
            aria-label="Close add properties"
            onClick={close}
            className="rounded px-1 text-lg leading-none text-slate-500 hover:bg-slate-100"
          >
            ×
          </button>
        </div>
        <div className="mt-2">
          <SearchBar
            variant="outlined"
            label="Search properties"
            testId="add-unassigned-search"
            placeholder="Search properties"
            value={filters.search}
            onChange={(search: string) => setFilters({ search })}
            onSearch={(search: string) => setFilters({ search })}
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 [&>div]:!mt-0">
          <DropdownSelect
            multiple
            size="sm"
            className="h-7 border-slate-300"
            label="Services"
            testId="add-unassigned-services"
            emptyLabel="All services"
            value={filters.serviceTypes}
            options={catalog.availableServices.map((service) => ({
              value: service.key,
              label: service.label,
            }))}
            onChange={(serviceTypes) => setFilters({ serviceTypes })}
          />
          {catalog.showRecency ? (
            <DropdownSelect
              size="sm"
              className="h-7 border-slate-300"
              label="Recency of service"
              testId="add-unassigned-recency"
              value={filters.recency ?? ""}
              options={RECENCY_OPTIONS[catalog.routeType].map((option) => ({
                value: option.value ?? "",
                label: option.value === null ? "Any time" : option.label,
              }))}
              onChange={(value) =>
                setFilters({ recency: (value || null) as RecencyFilter })
              }
            />
          ) : null}
          <DropdownSelect
            multiple
            size="sm"
            className="h-7 border-slate-300"
            label="Routes"
            testId="add-unassigned-routes"
            emptyLabel="Any route"
            value={filters.routeIds}
            options={catalog.routeOptions}
            onChange={(routeIds) => setFilters({ routeIds })}
          />
          <DropdownSelect
            size="sm"
            className="h-7 border-slate-300"
            icon={<span aria-hidden>↓↑</span>}
            label="Sort properties"
            testId="add-unassigned-sort"
            value={filters.sort}
            options={UNASSIGNED_SORT_OPTIONS}
            onChange={(sort) => setFilters({ sort: sort as UnassignedSort })}
          />
          <button
            type="button"
            data-testid="add-unassigned-reset"
            className="h-7 shrink-0 rounded-lg px-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            onClick={() => setFilters(defaultAddUnassignedFilters())}
          >
            Reset
          </button>
        </div>
      </header>
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-3 py-2 text-xs text-slate-600">
        <span>{catalog.properties.length} properties</span>
        <button
          type="button"
          className="font-semibold text-snow-blue-3 hover:underline disabled:opacity-40"
          disabled={
            eligible.length === 0 || catalog.isError || catalog.isLoading
          }
          onClick={() =>
            setSelected((previous) =>
              Array.from(
                new Map(
                  [...previous, ...eligible].map((property) => [
                    property.jobId,
                    property,
                  ]),
                ).values(),
              ),
            )
          }
        >
          Select all {eligible.length}
        </button>
      </div>
      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50 p-2">
        {catalog.isLoading ? (
          <li className="p-2 text-xs text-slate-500">Loading properties…</li>
        ) : catalog.isError ? (
          <li role="alert" className="p-2 text-xs text-rose-700">
            Could not load properties.
          </li>
        ) : catalog.properties.length === 0 ? (
          <li className="p-2 text-xs text-slate-500">
            No matching properties.
          </li>
        ) : (
          catalog.properties.map((property) => {
            const disabled = pooledIds.has(property.jobId);
            const checked = selectedIds.has(property.jobId);
            const routes = propertySearchRouteChips(property, session);
            return (
              <li key={property.jobId}>
                <label
                  className={`flex gap-2 rounded-lg border p-2 text-xs ${disabled ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500" : checked ? "cursor-pointer border-sky-500 bg-sky-50" : "cursor-pointer border-slate-200 bg-white hover:bg-sky-50"}`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-snow-blue-3"
                    disabled={disabled}
                    checked={checked}
                    data-testid={`add-unassigned-property-${property.jobId}`}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setSelected((previous) =>
                        checked
                          ? [...previous, property]
                          : previous.filter(
                              (candidate) => candidate.jobId !== property.jobId,
                            ),
                      );
                    }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">
                      {property.name}
                    </span>
                    <span className="mt-0.5 block text-slate-600">
                      {formatAddress(property.address)}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1 text-[10px]">
                      {(property.serviceTypes?.length
                        ? property.serviceTypes
                        : [property.serviceType]
                      )
                        .filter(Boolean)
                        .map((service) => (
                          <span
                            key={service}
                            className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700"
                          >
                            {serviceTypeLabel(service)}
                          </span>
                        ))}
                      {incorporatesTime(session.inputs) ? (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">
                          {formatEstimatedServiceTimeFromSeconds(
                            property.estimatedServiceTimeSeconds,
                          )}
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1 text-[10px]">
                      {routes.length ? (
                        routes.map((route) => {
                          const palette = resolveRouteColorPalette(route.color);
                          return (
                            <span
                              key={route.id}
                              className={`rounded px-1.5 py-0.5 ring-1 ring-inset ring-slate-200 ${palette.text} ${palette.bgLight}`}
                            >
                              {route.name}
                            </span>
                          );
                        })
                      ) : (
                        <span className="rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-slate-500">
                          Not on a route
                        </span>
                      )}
                      {disabled ? (
                        <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-amber-800">
                          In Unassigned
                        </span>
                      ) : null}
                    </span>
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
      <footer className="shrink-0 border-t border-slate-200 p-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-700">
            {selection.length} selected
          </span>
          <button
            type="button"
            className="font-semibold text-snow-blue-3 hover:underline"
            onClick={() => setSelected([])}
          >
            Clear
          </button>
          <SubtleButton
            buttonRole="primary"
            className="ml-auto !text-xs"
            disabled={selection.length === 0}
            onClick={() => {
              addPropertiesToUnassigned(selection);
              close();
            }}
          >
            Add {selection.length} to Unassigned
          </SubtleButton>
        </div>
      </footer>
    </div>,
    document.body,
  );
}
